import type { GraphDisplayConnection } from '../../../../core/policies/graph-display';
import type { VisibleRelationshipEdge } from '../../../../core/policies/explicit-relationships';
import type { JourneyNode } from '../../../../core/entities/node';
import type { GraphCamera } from '../../services/graph-camera';
import { applyAmbientDrift, nodeRadius } from '../../services/graph-interaction';
import {
  mixHexColors,
  presetForNodeType,
  toRgba,
  type GraphNodeTypeVisualConfig,
} from '../../services/graph-appearance-config';
export type PositionedNode = JourneyNode & { x: number; y: number; depth?: number };
export class GraphCanvasLayer {
  constructor(private canvas: HTMLCanvasElement) {}
  render(
    nodes: PositionedNode[],
    connections: GraphDisplayConnection[],
    visibleRelationshipEdges: VisibleRelationshipEdge[] = [],
    highlighted: GraphDisplayConnection[] = [],
    focusedNodeId: string | null = null,
    camera: GraphCamera = { x: 0, y: 0, velocityX: 0, velocityY: 0, scale: 1, targetX: 0, targetY: 0, targetScale: 1 },
    timeMs = 0,
    typeVisualConfig: Record<string, GraphNodeTypeVisualConfig> = {}
  ): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.save();
    ctx.translate(camera.x, camera.y);
    ctx.scale(camera.scale, camera.scale);

    const driftedNodes = applyAmbientDrift(nodes, timeMs);
    const byId = new Map(driftedNodes.map((node) => [node.nodeId, node]));

    for (const connection of connections) {
      const source = byId.get(connection.sourceNodeId);
      const target = byId.get(connection.targetNodeId);
      if (!source || !target) continue;

      const hot = connection.emphasis === 'focused' || highlighted.some((item) => item.connectionId === connection.connectionId);
      const dimmed = connection.emphasis === 'dimmed';
      const depthMix = ((source.depth ?? 1) + (target.depth ?? 1)) / 2;
      const opacity = dimmed ? 0.012 : 0.04 + depthMix * 0.04;
      ctx.strokeStyle = connectionStrokeStyle(connection, hot, opacity, dimmed);
      ctx.lineWidth = hot ? 2.0 / Math.max(0.9, camera.scale * 0.6) : (dimmed ? 0.6 : 1) / Math.max(0.9, camera.scale * 0.6);
      ctx.beginPath();
      ctx.moveTo(source.x, source.y);
      ctx.lineTo(target.x, target.y);
      ctx.stroke();
    }

    for (const edge of visibleRelationshipEdges) {
      const source = byId.get(edge.sourceNodeId);
      const target = byId.get(edge.targetNodeId);
      if (!source || !target) continue;

      const depthMix = ((source.depth ?? 1) + (target.depth ?? 1)) / 2;
      const deltaX = target.x - source.x;
      const deltaY = target.y - source.y;
      const length = Math.hypot(deltaX, deltaY) || 1;
      const normalX = (-deltaY / length) * 12;
      const normalY = (deltaX / length) * 12;
      const midpointX = (source.x + target.x) / 2;
      const midpointY = (source.y + target.y) / 2;
      const edgeVisibleScale = Math.max(0.9, camera.scale * 0.6);

      ctx.save();
      if ('setLineDash' in ctx && typeof ctx.setLineDash === 'function') {
        ctx.setLineDash(edge.summary.edgeKind === 'explicit-only' ? [10, 8] : []);
      }
      ctx.strokeStyle = relationshipStrokeStyle(0.24 + depthMix * 0.1);
      ctx.lineWidth = (edge.summary.edgeKind === 'explicit-only' ? 1.6 : 1.25) / edgeVisibleScale;
      ctx.beginPath();
      ctx.moveTo(source.x, source.y);
      ctx.lineTo(target.x, target.y);
      ctx.stroke();

      if ('setLineDash' in ctx && typeof ctx.setLineDash === 'function') {
        ctx.setLineDash([]);
      }

      ctx.fillStyle = 'rgba(255, 239, 214, 0.96)';
      ctx.font = edge.summary.hasOverflow
        ? '500 11px "Geist", "Satoshi", sans-serif'
        : '600 11px "Geist", "Satoshi", sans-serif';
      ctx.fillText(edge.summary.summaryText, midpointX + normalX, midpointY + normalY);
      if (edge.summary.edgeKind === 'mixed' && edge.summary.derivedReasonText) {
        ctx.fillStyle = 'rgba(255, 214, 143, 0.76)';
        ctx.font = '500 9px "Geist", "Satoshi", sans-serif';
        ctx.fillText(edge.summary.derivedReasonText, midpointX + normalX, midpointY + normalY + 13 / edgeVisibleScale);
      }
      ctx.restore();
    }

    const orderedNodes = [...driftedNodes].sort((left, right) => {
      if ((left.depth ?? 1) !== (right.depth ?? 1)) return (left.depth ?? 1) - (right.depth ?? 1);
      if (left.nodeId === focusedNodeId) return 1;
      if (right.nodeId === focusedNodeId) return -1;
      return 0;
    });

    for (const node of orderedNodes) {
      const depth = node.depth ?? 1;
      const radius = nodeRadius(node.nodeId, focusedNodeId) * depth;
      const focused = node.nodeId === focusedNodeId;
      const visual = typeVisualConfig[node.type] ?? presetForNodeType(node.type);
      const highlightColor = mixHexColors(visual.colorHex, '#ffffff', focused ? 0.66 : 0.46);
      const midColor = mixHexColors(visual.colorHex, '#dceeff', 0.28);
      const deepColor = mixHexColors(visual.colorHex, '#07101f', 0.6);
      const shadowColor = mixHexColors(visual.colorHex, '#03060d', 0.82);
      const labelColor = mixHexColors(visual.colorHex, '#f7fbff', 0.4);
      const labelVisible = focused || visual.labelsVisible;

      if (focused) {
        ctx.save();
        ctx.fillStyle = toRgba(highlightColor, 0.18);
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius + 12, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      ctx.save();
      // Outer shell shadow (diffused whisper shadow)
      ctx.shadowColor = focused
        ? toRgba(highlightColor, clamp(0.36 + visual.brightness * 0.2, 0.36, 0.74))
        : toRgba(visual.colorHex, clamp(0.08 + visual.brightness * 0.16, 0.08, 0.32));
      ctx.shadowBlur = focused ? 24 : 12 * depth;
      
      // Glass body
      const gradient = ctx.createRadialGradient(node.x - radius * 0.3, node.y - radius * 0.3, radius * 0.1, node.x, node.y, radius);
      gradient.addColorStop(0, toRgba(highlightColor, focused ? 0.96 : clamp(0.46 + visual.brightness * 0.22, 0.46, 0.82)));
      gradient.addColorStop(0.4, toRgba(midColor, focused ? 0.72 : clamp(0.26 + visual.brightness * 0.16, 0.24, 0.54)));
      gradient.addColorStop(1, toRgba(deepColor, focused ? 0.88 : clamp(0.48 + visual.brightness * 0.2, 0.46, 0.84)));
      ctx.fillStyle = gradient;
      
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
      ctx.fill();
      
      // Outer shell hairline
      ctx.strokeStyle = focused ? toRgba('#ffffff', 0.92) : toRgba(highlightColor, clamp(0.18 + visual.brightness * 0.16, 0.18, 0.42));
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Inner core refraction (Liquid Glass)
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius - 1.5, 0, Math.PI * 2);
      ctx.strokeStyle = focused ? toRgba(highlightColor, 0.64) : toRgba(midColor, clamp(0.08 + visual.brightness * 0.08, 0.08, 0.22));
      ctx.lineWidth = 1;
      ctx.stroke();
      
      ctx.restore();

	  if (!labelVisible) {
		continue;
	  }

      ctx.save();
      ctx.fillStyle = focused ? '#ffffff' : toRgba(labelColor, clamp(0.36 + depth * 0.3 + visual.brightness * 0.08, 0.34, 0.9));
      ctx.font = focused ? '500 14px "Geist", "Satoshi", sans-serif' : depth > 1 ? '500 12px "Geist", "Satoshi", sans-serif' : '400 10px "Geist", "Satoshi", sans-serif';
      ctx.fillText(node.displayName, node.x + radius + 12, node.y + 4);
      ctx.restore();
    }

    ctx.restore();
  }
}

function connectionStrokeStyle(connection: GraphDisplayConnection, hot: boolean, opacity: number, dimmed: boolean): string {
  if (connection.reasonCode === 'shared-tags') {
    return hot
      ? 'rgba(109, 221, 255, 0.86)'
      : `rgba(109, 221, 255, ${opacity * (dimmed ? 0.7 : 1.35)})`;
  }

  return hot ? 'rgba(255, 255, 255, 0.8)' : `rgba(255,255,255,${opacity})`;
}

function relationshipStrokeStyle(opacity: number): string {
  return `rgba(255, 195, 117, ${opacity})`;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function drawStarDust(ctx: CanvasRenderingContext2D, width: number, height: number, timeMs: number) {
  const time = timeMs / 1000;
  ctx.save();
  for (let index = 0; index < 48; index += 1) {
    const seed = index * 97.13;
    const x = ((seed * 47.2) % width + Math.sin(time * 0.02 + index) * 24 + width) % width;
    const y = ((seed * 89.4) % height + Math.cos(time * 0.018 + index * 0.6) * 18 + height) % height;
    const radius = 0.6 + (index % 3) * 0.35;
    const alpha = 0.12 + (index % 5) * 0.03;
    ctx.fillStyle = `rgba(178, 214, 255, ${alpha})`;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

